import React from 'react';
import {
  Box,
  Typography,
  Chip,
  IconButton,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import {
  AccessTime as AccessTimeIcon,
  VideoLibrary as VideoLibraryIcon,
  Slideshow as SlideshowIcon,
  Quiz as QuizIcon,
  MenuBook as MenuBookIcon,
  BookmarkBorder as BookmarkBorderIcon,
  Bookmark as BookmarkIcon,
  Message as MessageIcon,
  Inventory as InventoryIcon,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import DOMPurify from 'dompurify';
import { APP_URL } from '../../config';
import { htmlContentStyles } from '../../utils/stylesUtils';

const HeroContainer = styled(Box)(({ theme, backgroundImage }) => ({
  position: 'relative',
  backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5)), url(${backgroundImage})`,
  backgroundSize: 'cover',
  backgroundPosition: 'right center',
  backgroundRepeat: 'no-repeat',
  borderRadius: theme.spacing(1.5),
  color: 'white',
  padding: theme.spacing(4),
  marginBottom: theme.spacing(2),
  boxShadow: theme.shadows[4],
  minHeight: '300px',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  [theme.breakpoints.down('md')]: {
    padding: theme.spacing(3),
    minHeight: '250px',
  },
  [theme.breakpoints.up('md')]: {
    padding: theme.spacing(6),
    minHeight: '350px',
  },
}));

const ContentWrapper = styled(Box)(({ theme }) => ({
  maxWidth: '640px',
  zIndex: 2,
  position: 'relative',
}));

const StatsContainer = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(3),
  paddingTop: theme.spacing(3),
  borderTop: '1px solid rgba(255, 255, 255, 0.2)',
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: theme.spacing(0.5, 1),
  fontSize: '0.875rem',
}));

const StatItem = styled(Box)(({ theme, isClickable = false }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(1),
  borderRadius: theme.spacing(1),
  transition: 'background-color 0.2s ease',
  cursor: isClickable ? 'pointer' : 'default',
  '&:hover': isClickable ? {
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
  } : {},
}));

const ModuleChip = styled(Chip)(({ theme }) => ({
  backgroundColor: '#48BB78',
  color: 'white',
  fontWeight: 600,
  fontSize: '0.75rem',
  height: '24px',
  '& .MuiChip-label': {
    padding: '0 8px',
  },
}));

const LessonHeroSection = ({
  courseData,
  currentModule,
  currentLesson,
  moduleIndex,
  lessonIndex,
  isBookmarked,
  onToggleBookmark,
  onScrollToReviews,
  onOpenMaterials,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { t } = useTranslation(['lessons', 'courses']);
  const { user } = useAuth();

  const getLessonImage = () => {
    if (currentLesson?.image) {
      return currentLesson.image.startsWith('http')
        ? currentLesson.image
        : `${APP_URL}${currentLesson.image}`;
    }
    if (courseData?.image) {
      return courseData.image.startsWith('http')
        ? courseData.image
        : `${APP_URL}${courseData.image}`;
    }
    return `${APP_URL}/images/courses/default-${
      courseData?.category?.slug || 'default'
    }.jpg`;
  };

  const formatDuration = (minutes) => {
    if (!minutes) return '0 min';
    if (minutes < 60) {
      return `${minutes} min`;
    }
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return remainingMinutes > 0 ? `${hours}h ${remainingMinutes}m` : `${hours}h`;
  };

  const getLessonStats = () => {
    const stats = {
      duration: currentLesson?.duration || 0,
      hasVideo: false,
      hasPresentation: false,
      hasTests: false,
      testCount: 0,
      hasMaterials: false,
      materialsCount: 0,
    };

    // Проверяем наличие видео
    if (
      currentLesson?.type === 'video' ||
      currentLesson?.contentType === 'video' ||
      (currentLesson?.videoUrl && currentLesson.videoUrl.trim() !== '') ||
      (currentLesson?.contentBlocks &&
        currentLesson.contentBlocks.length > 0 &&
        currentLesson.contentBlocks.some((block) => block.type === 'video'))
    ) {
      stats.hasVideo = true;
    }

    // Проверяем наличие презентации
    if (
      currentLesson?.contentType === 'presentation' ||
      (currentLesson?.contentBlocks &&
        currentLesson.contentBlocks.length > 0 &&
        currentLesson.contentBlocks.some((block) => block.type === 'presentation'))
    ) {
      stats.hasPresentation = true;
    }

    // Проверяем наличие тестов
    if (currentLesson?.tests && currentLesson.tests.length > 0) {
      stats.hasTests = true;
      stats.testCount = currentLesson.tests.length;
    }

    // Проверяем наличие материалов
    if (currentLesson?.resources && currentLesson.resources.length > 0) {
      stats.hasMaterials = true;
      stats.materialsCount = currentLesson.resources.length;
    }

    return stats;
  };

  const getLessonPosition = () => {
    if (!courseData?.modules || moduleIndex === null || lessonIndex === null) {
      return null;
    }

    let totalLessons = 0;
    let currentLessonNumber = 0;

    // Сначала подсчитываем общее количество уроков во всех модулях
    for (let i = 0; i < courseData.modules.length; i++) {
      const module = courseData.modules[i].module;
      if (module.lessons && module.lessons.length > 0) {
        totalLessons += module.lessons.length;
      }
    }

    // Затем подсчитываем позицию текущего урока среди всех уроков
    for (let i = 0; i <= moduleIndex; i++) {
      const module = courseData.modules[i].module;
      if (module.lessons && module.lessons.length > 0) {
        if (i === moduleIndex) {
          // Это текущий модуль - добавляем позицию урока в этом модуле
          currentLessonNumber += lessonIndex + 1;
        } else {
          // Это предыдущие модули - добавляем все их уроки
          currentLessonNumber += module.lessons.length;
        }
      }
    }

    return { current: currentLessonNumber, total: totalLessons };
  };

  const lessonStats = getLessonStats();
  const lessonPosition = getLessonPosition();

  if (!currentLesson) {
    return null;
  }

  return (
    <HeroContainer backgroundImage={getLessonImage()}>
      <ContentWrapper>
        {/* Module Badge - только если есть данные модуля */}
        {courseData && currentModule && moduleIndex !== null && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <ModuleChip
              label={`${t('lessons:lessonDetailPage.module')} ${moduleIndex + 1}: ${currentModule.title?.toUpperCase()}`}
            />
          </Box>
        )}

        {/* Lesson Title */}
        <Typography
          variant={isMobile ? 'h4' : 'h3'}
          component="h1"
          sx={{
            fontWeight: 700,
            mb: 2,
            lineHeight: 1.2,
          }}
        >
          {currentLesson.title}
        </Typography>

        {/* Lesson Description */}
        {currentLesson.description && (
          <Box
            component="div"
            sx={{
              color: 'rgba(255, 255, 255, 0.8)',
              lineHeight: 1.6,
              mb: 3,
              fontSize: '1rem',
              ...htmlContentStyles,
              // Override some styles for better visibility on dark background
              '& h1, & h2, & h3, & h4, & h5, & h6': {
                color: 'rgba(255, 255, 255, 0.95)',
              },
              '& a': {
                color: '#90caf9',
              },
              '& code': {
                backgroundColor: 'rgba(0, 0, 0, 0.3)',
              },
              '& pre': {
                backgroundColor: 'rgba(0, 0, 0, 0.3)',
              },
            }}
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(currentLesson.description),
            }}
          />
        )}
      </ContentWrapper>

      {/* Lesson Statistics */}
      <StatsContainer>
        {/* Duration */}
        <StatItem>
          <AccessTimeIcon sx={{ fontSize: 16, mr: 1 }} />
          {formatDuration(lessonStats.duration)}
        </StatItem>

        {/* Videos */}
        {lessonStats.hasVideo && (
          <StatItem>
            <VideoLibraryIcon sx={{ fontSize: 16, mr: 1 }} />
            {t('lessons:lessonDetailPage.hasVideo')}
          </StatItem>
        )}

        {/* Presentations */}
        {lessonStats.hasPresentation && (
          <StatItem>
            <SlideshowIcon sx={{ fontSize: 16, mr: 1 }} />
            {t('lessons:lessonDetailPage.hasPresentation')}
          </StatItem>
        )}

        {/* Tests */}
        {lessonStats.hasTests && (
          <StatItem>
            <QuizIcon sx={{ fontSize: 16, mr: 1 }} />
            {lessonStats.testCount} {t('lessons:lessonDetailPage.tests')}
          </StatItem>
        )}

        {/* Materials */}
        {lessonStats.hasMaterials && onOpenMaterials && (
          <StatItem
            isClickable={true}
            onClick={onOpenMaterials}
          >
            <InventoryIcon sx={{ fontSize: 16, mr: 1 }} />
            {t('lessons:lessonDetailPage.materials')}
          </StatItem>
        )}

        {/* Spacer */}
        <Box sx={{ flexGrow: 1 }} />

        {/* Reviews - Clickable - только если есть данные курса */}
        {courseData && onScrollToReviews && (
          <StatItem isClickable={true} onClick={onScrollToReviews}>
            <MessageIcon sx={{ fontSize: 16, mr: 1 }} />
            {t('lessons:lessonDetailPage.reviews')}
          </StatItem>
        )}

        {/* Lesson Position - только если есть данные курса */}
        {lessonPosition && courseData && (
          <StatItem>
            <MenuBookIcon sx={{ fontSize: 16, mr: 1 }} />
            {t('lessons:lessonDetailPage.lessonPosition', {
              current: lessonPosition.current,
              total: lessonPosition.total,
            })}
          </StatItem>
        )}

        {/* Bookmark Button */}
        {user && (
          <IconButton
            onClick={onToggleBookmark}
            sx={{
              color: 'white',
              padding: 1,
              borderRadius: theme.spacing(1),
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
              },
            }}
          >
            {isBookmarked ? (
              <BookmarkIcon sx={{ fontSize: 20 }} />
            ) : (
              <BookmarkBorderIcon sx={{ fontSize: 20 }} />
            )}
          </IconButton>
        )}
      </StatsContainer>
    </HeroContainer>
  );
};

export default LessonHeroSection;
